/**
 * People Pixels - Client Connection Handler
 *
 * Robust WebSocket connection with:
 * - Auto-reconnect with exponential backoff
 * - Event handling for all pixel events
 * - Vibration (Android) + visual fallback (iOS)
 * - Connection state tracking
 * - Heartbeat/pong responses
 */

// ============================================
// TYPES
// ============================================

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export type PixelEventType = 'BUZZ' | 'COLOR' | 'FLASH' | 'WAVE' | 'CLEAR';

export interface PixelEvent {
  id: string;
  type: PixelEventType;
  timestamp: number;
  pattern?: number[];    // For BUZZ
  color?: string;        // For COLOR, FLASH, WAVE
  duration?: number;     // For COLOR
  count?: number;        // For FLASH
  interval?: number;     // For FLASH
  direction?: string;    // For WAVE
  speed?: number;        // For WAVE
}

export interface WelcomePayload {
  connectionId: string;
  sectionId: string;
  serverTime: number;
  currentEvent: PixelEvent | null;
}

export interface PixelConnectionConfig {
  serverUrl: string;
  section: string;
  seatInfo?: {
    section?: string;
    row?: string;
    number?: string;
  };
  onStateChange?: (state: ConnectionState) => void;
  onEvent?: (event: PixelEvent) => void;
  onError?: (error: string) => void;
  onConnectionInfo?: (info: WelcomePayload) => void;
}

// ============================================
// CONSTANTS
// ============================================

const MAX_RECONNECT_DELAY = 30000; // 30 seconds max
const INITIAL_RECONNECT_DELAY = 1000; // Start at 1 second
const RECONNECT_MULTIPLIER = 2;

// Default vibration pattern (3 buzzes)
const DEFAULT_BUZZ_PATTERN = [200, 100, 200, 100, 200];

// ============================================
// PIXEL CONNECTION CLASS
// ============================================

export class PixelConnection {
  private config: PixelConnectionConfig;
  private ws: WebSocket | null = null;
  private state: ConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private connectionId: string | null = null;
  private currentColor: string | null = null;

  constructor(config: PixelConnectionConfig) {
    this.config = config;
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Get connection ID (assigned by server)
   */
  getConnectionId(): string | null {
    return this.connectionId;
  }

  /**
   * Get current screen color
   */
  getCurrentColor(): string | null {
    return this.currentColor;
  }

  /**
   * Connect to the pixel server
   */
  connect(): void {
    if (this.state === 'connected' || this.state === 'connecting') {
      console.log('[PixelConnection] Already connected or connecting');
      return;
    }

    this.setState('connecting');
    this.createWebSocket();
  }

  /**
   * Disconnect from the server
   */
  disconnect(): void {
    this.clearReconnectTimeout();
    this.reconnectAttempts = 0;

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.setState('disconnected');
  }

  /**
   * Create WebSocket connection
   */
  private createWebSocket(): void {
    const url = `${this.config.serverUrl}/section/${this.config.section}`;
    console.log(`[PixelConnection] Connecting to ${url}`);

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('[PixelConnection] WebSocket creation error:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket open
   */
  private handleOpen(): void {
    console.log('[PixelConnection] Connected');
    this.setState('connected');
    this.reconnectAttempts = 0;

    // Register seat info if provided
    if (this.config.seatInfo) {
      this.send({
        type: 'register',
        payload: this.config.seatInfo,
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data);

      switch (message.type) {
        case 'welcome':
          this.handleWelcome(message.payload);
          break;

        case 'event':
          this.handlePixelEvent(message.payload);
          break;

        case 'heartbeat':
          this.handleHeartbeat();
          break;

        case 'state':
          // State update
          if (message.payload.currentEvent) {
            this.handlePixelEvent(message.payload.currentEvent);
          }
          break;

        case 'error':
          console.error('[PixelConnection] Server error:', message.payload);
          this.config.onError?.(message.payload);
          break;

        default:
          console.log('[PixelConnection] Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('[PixelConnection] Message parse error:', error);
    }
  }

  /**
   * Handle welcome message
   */
  private handleWelcome(payload: WelcomePayload): void {
    console.log('[PixelConnection] Welcome:', payload);
    this.connectionId = payload.connectionId;
    this.config.onConnectionInfo?.(payload);

    // Apply current event if there is one
    if (payload.currentEvent) {
      this.handlePixelEvent(payload.currentEvent);
    }
  }

  /**
   * Handle heartbeat (respond with pong)
   */
  private handleHeartbeat(): void {
    this.send({
      type: 'pong',
      timestamp: Date.now(),
    });
  }

  /**
   * Handle pixel event
   */
  private handlePixelEvent(event: PixelEvent): void {
    console.log('[PixelConnection] Event received:', event.type, event.id);

    // Notify callback
    this.config.onEvent?.(event);

    // Execute the event
    switch (event.type) {
      case 'BUZZ':
        this.executeBuzz(event.pattern || DEFAULT_BUZZ_PATTERN);
        break;

      case 'COLOR':
        this.executeColor(event.color || '#FF6600', event.duration);
        break;

      case 'FLASH':
        this.executeFlash(event.color || '#FFFFFF', event.count || 3, event.interval || 200);
        break;

      case 'WAVE':
        this.executeWave(event.color || '#FF6600', event.direction || 'right', event.speed || 200);
        break;

      case 'CLEAR':
        this.executeClear();
        break;
    }

    // Send acknowledgment
    this.send({
      type: 'ack',
      payload: { eventId: event.id },
      timestamp: Date.now(),
    });
  }

  /**
   * Execute BUZZ - Vibrate the phone
   */
  private executeBuzz(pattern: number[]): void {
    console.log('[PixelConnection] BUZZ!', pattern);

    // Try vibration API (Android)
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
        console.log('[PixelConnection] Vibration triggered');
      } catch (error) {
        console.warn('[PixelConnection] Vibration failed:', error);
      }
    }

    // Always do visual feedback (works on iOS too)
    this.executeFlash('#FFFFFF', 3, 100);
  }

  /**
   * Execute COLOR - Set screen color
   */
  private executeColor(color: string, duration?: number): void {
    console.log('[PixelConnection] COLOR:', color);
    this.currentColor = color;

    // Dispatch custom event for UI to handle
    window.dispatchEvent(new CustomEvent('pixel-color', {
      detail: { color, duration }
    }));

    // If duration specified, clear after
    if (duration) {
      setTimeout(() => {
        this.currentColor = null;
        window.dispatchEvent(new CustomEvent('pixel-color', {
          detail: { color: null }
        }));
      }, duration);
    }
  }

  /**
   * Execute FLASH - Flash screen multiple times
   */
  private executeFlash(color: string, count: number, interval: number): void {
    console.log('[PixelConnection] FLASH:', color, count, interval);

    let flashes = 0;
    const flashInterval = setInterval(() => {
      if (flashes >= count * 2) {
        clearInterval(flashInterval);
        window.dispatchEvent(new CustomEvent('pixel-flash', {
          detail: { active: false }
        }));
        return;
      }

      const active = flashes % 2 === 0;
      window.dispatchEvent(new CustomEvent('pixel-flash', {
        detail: { active, color }
      }));
      flashes++;
    }, interval);
  }

  /**
   * Execute WAVE - Part of coordinated wave
   */
  private executeWave(color: string, direction: string, speed: number): void {
    console.log('[PixelConnection] WAVE:', direction, color);

    // For now, just flash the color
    // In future, could coordinate timing based on section position
    this.executeColor(color, speed * 3);
  }

  /**
   * Execute CLEAR - Reset to default
   */
  private executeClear(): void {
    console.log('[PixelConnection] CLEAR');
    this.currentColor = null;
    window.dispatchEvent(new CustomEvent('pixel-clear'));
  }

  /**
   * Handle WebSocket close
   */
  private handleClose(event: CloseEvent): void {
    console.log(`[PixelConnection] Disconnected (code: ${event.code}, reason: ${event.reason})`);
    this.ws = null;

    if (this.state !== 'disconnected') {
      // Unexpected close - reconnect
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket error
   */
  private handleError(event: Event): void {
    console.error('[PixelConnection] WebSocket error:', event);
    this.config.onError?.('WebSocket error');
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimeout();
    this.setState('reconnecting');

    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(RECONNECT_MULTIPLIER, this.reconnectAttempts),
      MAX_RECONNECT_DELAY
    );

    console.log(`[PixelConnection] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.createWebSocket();
    }, delay);
  }

  /**
   * Clear reconnect timeout
   */
  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /**
   * Set connection state and notify callback
   */
  private setState(state: ConnectionState): void {
    if (this.state !== state) {
      this.state = state;
      this.config.onStateChange?.(state);
    }
  }

  /**
   * Send message to server
   */
  private send(message: object): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

let pixelConnection: PixelConnection | null = null;

export function getPixelConnection(): PixelConnection | null {
  return pixelConnection;
}

export function createPixelConnection(config: PixelConnectionConfig): PixelConnection {
  if (pixelConnection) {
    pixelConnection.disconnect();
  }
  pixelConnection = new PixelConnection(config);
  return pixelConnection;
}
